import { activeOrdersAtom, recentOrdersAtom } from "@/atoms/active-orders"
import availabilitiesAtom from "@/atoms/availabilities"
import itemsAtom from "@/atoms/items"
import optionGroupsAtom from "@/atoms/option-groups"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { reconstructCartItemsFromOrder, ReorderResult } from "@/lib/reorder-service"
import { OrderResponse } from "@/types/external"
import { useAtomValue } from "jotai"
import { useMemo } from "react"

export interface ReorderableOrder extends ReorderResult {
  order: OrderResponse
}

/**
 * Reconstructs the catalog snapshot for each of the last terminal orders,
 * against the browsing channel's catalog (INDOOR menu at a table).
 *
 * Returns an empty array while:
 * - the customer has any in-progress order (the duplicate-guard handles
 *   reorder-while-running, so the section is hidden),
 * - the catalog hasn't loaded yet,
 * - or every recent order had all of its items invalidated by the
 *   availability cascade.
 */
export function useRecentReorderableOrders(): ReorderableOrder[] {
  const recent = useAtomValue(recentOrdersAtom)
  const activeOrders = useAtomValue(activeOrdersAtom)
  const items = useAtomValue(itemsAtom)
  const { itemOffers, categories } = useBrowsingChannelCatalog()
  const optionGroups = useAtomValue(optionGroupsAtom)
  const availabilities = useAtomValue(availabilitiesAtom)

  return useMemo(() => {
    if (recent.length === 0) return []
    if (activeOrders.length > 0) return []
    if (items.length === 0 || itemOffers.length === 0) return []

    const reorderable: ReorderableOrder[] = []
    for (const order of recent) {
      const result = reconstructCartItemsFromOrder(
        order,
        items,
        itemOffers,
        optionGroups,
        categories,
        availabilities,
      )
      if (result.cartItems.length === 0) continue
      reorderable.push({ order, ...result })
    }
    return reorderable
  }, [recent, activeOrders, items, itemOffers, optionGroups, categories, availabilities])
}
